/* ======================================================================
 * WHEN AN ORDERED LIST IS ALLOWED TO OWN THE TRANSITIONS
 * ======================================================================
 *
 * DECISIONS01 §9 answer 5 settled the builder canvas as "an ordered
 * list, reusing StageEditor.jsx" rather than a node-and-edge canvas.
 * That is the right shape for what is actually in the catalogue — all
 * 57 seeded definitions are strictly linear, checked rather than
 * assumed: each step's `next` is the following step by seq and the last
 * has none, across all 330 steps.
 *
 * BUT THE SCHEMA IS NOT LINEAR. workflow_transitions carries
 * `condition`, `label` and `is_loop`; V5 exists precisely so an intended
 * loop can be told from an accidental one; `gate` and `condition` are
 * step types. So a graph with a branch is expressible, and the day a
 * tenant forks a definition and adds one, an ordered list that derives
 * the edges from row order WOULD SILENTLY DELETE IT — the author would
 * drag a row, press Save, and lose a branch nothing on screen ever
 * showed them.
 *
 * That is the failure this module exists to prevent, and it is the same
 * failure the seeder refused when it chose skip over upsert: a write
 * that destroys work the screen never displayed.
 *
 * So: isLinearChain() asks whether the ordered list is a FAITHFUL
 * representation of this particular graph. When it is, reordering,
 * adding and removing rewire the chain. When it is not, the builder
 * locks topology editing and says why — field edits (name, type, actor,
 * SLA, timeout, config), which are the whole of the A3 review pass,
 * stay available on any graph shape because they touch no edges.
 * ====================================================================== */

/** True when `transitions` is exactly the chain implied by step order.
 *
 *  Exactly, in both directions: every consecutive pair is joined, the
 *  last step is terminal, one entry edge points at the first step, and
 *  there is nothing else — no extra edge, no condition, no loop, no
 *  label. Anything else means the list is showing less than the graph
 *  contains.
 */
export function isLinearChain(steps, transitions) {
  if (!steps || steps.length === 0) return true

  const entries = transitions.filter((t) => t.from == null)
  if (entries.length !== 1 || entries[0].to !== steps[0].key) return false

  const forward = transitions.filter((t) => t.from != null)
  if (forward.length !== steps.length - 1) return false

  const byFrom = new Map()
  for (const t of forward) {
    if (byFrom.has(t.from)) return false // a branch
    if (t.condition || t.is_loop || t.label) return false
    byFrom.set(t.from, t.to)
  }

  for (let i = 0; i < steps.length - 1; i += 1) {
    if (byFrom.get(steps[i].key) !== steps[i + 1].key) return false
  }
  return !byFrom.has(steps[steps.length - 1].key)
}

/** The chain implied by the current order, plus the entry edge.
 *
 *  THE ENTRY EDGE IS THE FIRST ROW, which is D13's rule stated in the
 *  UI's terms: the seeder takes entry from the LOWEST SEQ and not from
 *  the trigger-typed step, because 16 workflows open with a trigger and
 *  5 carry one mid-graph. Row one is row one. */
export function chainTransitions(steps) {
  if (!steps || steps.length === 0) return []
  const out = [{ from: null, to: steps[0].key, label: null, condition: null, is_loop: false }]
  for (let i = 0; i < steps.length - 1; i += 1) {
    out.push({
      from: steps[i].key,
      to: steps[i + 1].key,
      label: null,
      condition: null,
      is_loop: false,
    })
  }
  return out
}

/** Renumber seq to match array position. The order IS the configuration;
 *  seq is how the database records it, and load_graph() orders by it. */
export function resequence(steps) {
  return steps.map((s, i) => ({ ...s, seq: i + 1 }))
}

/** Findings for one step, from the flat list the server returns. */
export function findingsFor(findings, stepKey) {
  return (findings || []).filter((f) => f.step_key === stepKey)
}

/** Findings that name no step — they are about the version as a whole
 *  (V10's missing cron, V11's placeholder event, V12's disabled module)
 *  and have no row to sit on. They belong beside the trigger fields,
 *  which is where the fix is. */
export function versionFindings(findings) {
  return (findings || []).filter((f) => !f.step_key)
}
